import React, { useRef, useState } from 'react';

import { Button } from 'common-src/components/base';
import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';

import styles from './XifinDocumentUpload.module.scss';

const SUPPORTED_EXTENSIONS = ['.csv', '.xlsx'];
const DEFAULT_ERROR = 'Unable to queue XIFIN document upload. Please try again.';
const FILE_ERROR = 'Please select a CSV or XLSX file.';
const XIFIN_TEMPLATE_URL =
  'https://docs.google.com/spreadsheets/d/13M_j4mv5aZC_Qo6L8vW9IFnKtQKLsDUPbzu8cnv7TdA/edit?usp=sharing';

export const isSupportedXifinFile = (file) => {
  const filename = file?.name?.toLowerCase() || '';
  return SUPPORTED_EXTENSIONS.some((extension) => filename.endsWith(extension));
};

const XifinDocumentUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0] || null;
    setResult(null);

    if (file && !isSupportedXifinFile(file)) {
      setSelectedFile(null);
      setError(FILE_ERROR);
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedFile || isUploading) return;

    if (!isSupportedXifinFile(selectedFile)) {
      setError(FILE_ERROR);
      return;
    }

    setIsUploading(true);
    setError('');
    setResult(null);

    const body = new FormData();
    body.append('sheet', selectedFile);

    try {
      const response = await apiRequest({
        endpoint: 'jobRequests/xifinDocumentUpload',
        method: HttpMethods.Post,
        body,
      });
      const responseBody = await response.json();

      if (!response.ok) {
        setError(getRestErrorMessage(responseBody) || DEFAULT_ERROR);
        return;
      }

      setResult(responseBody);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      setError(DEFAULT_ERROR);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.title}>Bulk XIFIN Document Upload</h2>
      <p className={styles.instructions}>
        Upload a CSV or XLSX sheet with the columns <strong>Patient ID</strong>,{' '}
        <strong>Accn ID</strong>, and <strong>Date of Service</strong>.
      </p>
      <p className={styles.instructions}>
        Open the{' '}
        <a href={XIFIN_TEMPLATE_URL} target="_blank" rel="noopener noreferrer">
          XIFIN document upload template
        </a>
        , download a copy, update it with the required data, and then upload the completed file
        below.
      </p>
      <div className={styles.controls}>
        <label className={styles.fileLabel} htmlFor="xifin-document-sheet">
          <span>XIFIN document sheet</span>
          <input
            ref={fileInputRef}
            id="xifin-document-sheet"
            type="file"
            accept=".csv,.xlsx"
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </label>
        <Button
          id="xifin-document-upload"
          text={isUploading ? 'Uploading...' : 'Upload'}
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          isLoading={isUploading}
        />
      </div>
      {isUploading && (
        <p className={styles.status} role="status">
          Queuing the sheet. Processing will continue in the background.
        </p>
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {result && (
        <p className={styles.success} role="status">
          {result.rows} rows were queued for XIFIN document processing in {result.batches}{' '}
          {result.batches === 1 ? 'batch' : 'batches'}. Processing will continue in the background.
        </p>
      )}
    </section>
  );
};

export default XifinDocumentUpload;
